import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ApiGet, ApiPost } from "../../helper/axiosFunctions";
import { commonToast } from "../../helper/Toast";
import { IoSearchOutline, IoArrowBack } from "react-icons/io5";
import { BsFolder2Open, BsCardText } from "react-icons/bs";
import { FiCheck } from "react-icons/fi";
import Loading from "../Loading";
import Button from "../ui/Button";

export default function ImportCardFromDeckModal({
    isOpen,
    setIsOpen,
    currentDeckId,
    onSuccess,
}) {
    const { t } = useTranslation();

    const [step, setStep] = useState('selectDeck');

    const [allDecks, setAllDecks] = useState([]);
    const [loadingDecks, setLoadingDecks] = useState(false);
    const [selectedSourceDeck, setSelectedSourceDeck] = useState(null);
    const [deckSearchQuery, setDeckSearchQuery] = useState('');
    const searchTimerRef = useRef(null);

    const [cards, setCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [selectedCards, setSelectedCards] = useState([]);
    const [cardSearchQuery, setCardSearchQuery] = useState('');
    const [importing, setImporting] = useState(false);

    const [sourceDeckDetails, setSourceDeckDetails] = useState(null);

    const fetchAllDecks = useCallback(async (query = '') => {
        setLoadingDecks(true);
        try {
            const response = await ApiPost(`/contents/decks/search?pageSize=50`, {
                deckName: query,
                status: "PUBLISHED",
            });
            const rawDecks = response?.data || [];
            const filtered = rawDecks.filter(deck => {
                const status = deck?.status?.toUpperCase();
                const hidden = ["DELETED", "INACTIVE", "REMOVED", "ARCHIVED", "DISABLED", "HIDDEN"];
                return !hidden.includes(status) && deck.deckId !== currentDeckId;
            });
            setAllDecks(filtered);
        } catch (error) {
            commonToast(error?.message || 'Failed to fetch decks', 'error');
        } finally {
            setLoadingDecks(false);
        }
    }, [currentDeckId]);

    const fetchSourceCards = useCallback(async (deckId) => {
        setLoadingCards(true);
        try {
            const response = await ApiGet(`/contents/decks/${deckId}/cards`);
            setCards(response?.data || []);
        } catch (error) {
            commonToast(error?.message || 'Failed to fetch cards', 'error');
            setCards([]);
        } finally {
            setLoadingCards(false);
        }
    }, []);

    const fetchSourceDeckDetails = useCallback(async (deckId) => {
        try {
            const response = await ApiGet(`/contents/decks/${deckId}`);
            setSourceDeckDetails(response?.data || null);
        } catch {
            setSourceDeckDetails(null);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setStep('selectDeck');
            setSelectedSourceDeck(null);
            setSelectedCards([]);
            setCards([]);
            setDeckSearchQuery('');
            setCardSearchQuery('');
            setSourceDeckDetails(null);
            setAllDecks([]);
            fetchAllDecks();
        }
    }, [isOpen, fetchAllDecks]);

    useEffect(() => {
        if (!isOpen || step !== 'selectDeck') return;

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            fetchAllDecks(deckSearchQuery);
        }, 400);

        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, [deckSearchQuery, isOpen, step, fetchAllDecks]);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSelectDeck = (deck) => {
        setSelectedSourceDeck(deck);
        setSelectedCards([]);
        setCardSearchQuery('');
        setStep('selectCards');
        fetchSourceCards(deck.deckId);
        fetchSourceDeckDetails(deck.deckId);
    };

    const handleBackStep = () => {
        setStep('selectDeck');
        setSelectedCards([]);
        setCards([]);
        setCardSearchQuery('');
        setSourceDeckDetails(null);
    };

    const toggleCardSelection = (card) => {
        setSelectedCards(prev => {
            const exists = prev.find(c => c.cardId === card.cardId);
            if (exists) return prev.filter(c => c.cardId !== card.cardId);
            return [...prev, card];
        });
    };

    const handleSelectAllCards = () => {
        if (selectedCards.length === filteredCards.length) {
            setSelectedCards([]);
        } else {
            setSelectedCards([...filteredCards]);
        }
    };

    const complexityMap = useMemo(() => {
        if (!sourceDeckDetails?.cards) return {};
        const map = {};
        sourceDeckDetails.cards.forEach(c => {
            map[c.cardId] = c.complexity;
        });
        return map;
    }, [sourceDeckDetails]);

    const handleImport = async () => {
        if (selectedCards.length === 0) return;
        setImporting(true);
        let successCount = 0;
        let errorCount = 0;

        let nextSortOrder = 0;
        try {
            const deckRes = await ApiGet(`/contents/decks/${currentDeckId}`);
            const orders = (deckRes?.data?.cards || [])
                .map((card) => Number(card?.sortOrder))
                .filter((value) => Number.isFinite(value));
            nextSortOrder = orders.length ? Math.max(...orders) + 1 : 0;
        } catch {
            nextSortOrder = 0;
        }

        for (const card of selectedCards) {
            try {
                await ApiPost('/contents/decks/cards', {
                    deckId: currentDeckId,
                    card: {
                        cardId: card.cardId,
                        complexity: complexityMap[card.cardId] || 3,
                        sortOrder: nextSortOrder,
                    },
                });
                nextSortOrder += 1;
                successCount++;
            } catch (error) {
                errorCount++;
                const errorMsg = error?.data?.errorDescription || error?.data?.error || error?.message || 'Import failed';
                commonToast(`${card.cardName || card.cardId}: ${errorMsg}`, 'error');
            }
        }

        if (successCount > 0) {
            commonToast(`${successCount} ${t('cardsImportedSuccess')}`, 'success');
            handleClose();
            if (onSuccess) onSuccess();
        }

        if (errorCount > 0 && successCount === 0) {
            handleClose();
        }

        setImporting(false);
    };

    const filteredDecks = useMemo(() => {
        return allDecks;
    }, [allDecks]);

    const filteredCards = useMemo(() => {
        if (!cardSearchQuery.trim()) return cards;
        const q = cardSearchQuery.toLowerCase();
        return cards.filter(c =>
            (c.cardName || '').toLowerCase().includes(q) ||
            (c.shortDescription || '').toLowerCase().includes(q)
        );
    }, [cards, cardSearchQuery]);

    const renderCardItem = (card) => {
        const isSelected = selectedCards.some(c => c.cardId === card.cardId);

        const questionImage = card?.question?.parts?.find(
            (part) => (part.contentType?.includes("image") || part.contentType?.includes("unknown"))
        )?.contentBody;
        const question = card?.question?.parts?.find(
            (part) => part.contentType?.includes("text")
        )?.contentBody;
        const answer = card?.answers?.[0]?.parts?.find(
            (part) => part.contentType?.includes("text")
        )?.contentBody;

        const cardComplexity = complexityMap[card.cardId];

        return (
            <div
                key={card.cardId}
                onClick={() => toggleCardSelection(card)}
                className={`rounded-xl border shadow-sm p-4 bg-white flex flex-col gap-2 relative cursor-pointer transition-all duration-150 ${
                    isSelected ? 'ring-2 ring-blue-500 border-blue-400 bg-blue-50/30' : 'hover:border-gray-300'
                }`}
            >
                <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                }`}>
                    {isSelected && <FiCheck className="text-white text-sm" />}
                </div>

                <div className="pr-8">
                    <h3 className="text-base font-semibold text-gray-800">
                        {card.cardName || 'Unnamed Card'}
                    </h3>
                </div>

                <div className="flex justify-between gap-2 text-xs text-gray-500">
                    <span className="capitalize">
                        {t("answerType")}: {card?.answerType?.toLowerCase() || 'N/A'}
                    </span>
                    <span>
                        {t("complexity")}: {cardComplexity !== undefined ? cardComplexity : 'N/A'}
                    </span>
                </div>

                <hr className="my-1" />

                {question && (
                    <div>
                        <h4 className="text-xs font-semibold text-gray-600 mb-1">{t("question")}:</h4>
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: question }} />
                    </div>
                )}

                {questionImage && (
                    <div className="my-1">
                        <img
                            src={questionImage}
                            alt="question"
                            loading="lazy"
                            className="max-w-full h-auto max-h-40 object-cover rounded-lg border text-sm"
                        />
                    </div>
                )}

                {answer && (
                    <div>
                        <h4 className="text-xs font-semibold text-gray-600 mb-1">{t("answer")}:</h4>
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: answer }} />
                    </div>
                )}

                {card?.hints && card.hints.length > 0 && (
                    <div>
                        <h4 className="text-xs font-semibold text-gray-600 mb-1">{t("Prompter")}:</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                            {card.hints.flatMap((hint, hi) =>
                                hint.parts.map((part, pi) => {
                                    if (part.contentType?.startsWith("text")) {
                                        return <div key={`${hi}-${pi}`} dangerouslySetInnerHTML={{ __html: part.contentBody }} />;
                                    }
                                    if (part.contentType?.startsWith("image")) {
                                        return <img key={`${hi}-${pi}`} src={part.contentBody} alt="Hint" className="max-w-full h-auto max-h-32 object-cover rounded-lg border my-1" />;
                                    }
                                    return null;
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderDeckItem = (deck) => (
        <div
            key={deck.deckId}
            onClick={() => handleSelectDeck(deck)}
            className="flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition-all"
        >
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                {deck.smallImageUrl ? (
                    <img
                        src={deck.smallImageUrl}
                        alt={deck.deckName}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <BsFolder2Open className="text-gray-400" size={20} />
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-800 truncate">
                    {deck.deckName || 'Unnamed Deck'}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                    {deck.mainSubject && (
                        <span className="text-xs text-gray-500">
                            {deck.mainSubject}
                        </span>
                    )}
                    {deck.cards && (
                        <span className="text-xs text-gray-400">
                            &bull; {deck.cards.length} {t('cards')}
                        </span>
                    )}
                </div>
            </div>
            <IoArrowBack className="text-gray-300 rotate-180 flex-shrink-0" size={18} />
        </div>
    );

    return (
        <Dialog
            open={isOpen}
            as="div"
            className="relative z-40 focus:outline-none"
            onClose={handleClose}
        >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className={`w-full relative rounded-2xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0 shadow-2xl ${
                            step === 'selectCards' ? 'max-w-4xl' : 'max-w-lg'
                        }`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                {step === 'selectCards' && (
                                    <button
                                        type="button"
                                        onClick={handleBackStep}
                                        className="p-1.5 hover:bg-gray-100 rounded-full transition-all"
                                    >
                                        <IoArrowBack size={20} className="text-gray-500" />
                                    </button>
                                )}
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">
                                        {step === 'selectDeck'
                                            ? t('importCardsFromDeck')
                                            : t('selectCardsToImport')
                                        }
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        {step === 'selectDeck'
                                            ? t('selectSourceDeck')
                                            : (selectedSourceDeck?.deckName || 'Source Deck')
                                        }
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="p-2 hover:bg-gray-100 rounded-full transition-all"
                            >
                                <IoMdClose size={22} className="text-gray-500" />
                            </button>
                        </div>

                        {/* Search */}
                        <div className="px-6 py-3 border-b border-gray-100">
                            <div className="relative">
                                <IoSearchOutline
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                                    size={20}
                                />
                                <input
                                    type="text"
                                    value={step === 'selectDeck' ? deckSearchQuery : cardSearchQuery}
                                    onChange={(e) => step === 'selectDeck'
                                        ? setDeckSearchQuery(e.target.value)
                                        : setCardSearchQuery(e.target.value)
                                    }
                                    placeholder={step === 'selectDeck'
                                        ? t('searchDecks')
                                        : t('searchCards')
                                    }
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
                                />
                            </div>
                        </div>

                        {/* Select All bar for cards step */}
                        {step === 'selectCards' && !loadingCards && filteredCards.length > 0 && (
                            <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                    {selectedCards.length} / {filteredCards.length} {t('selected')}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleSelectAllCards}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-all"
                                >
                                    {selectedCards.length === filteredCards.length
                                        ? t('deselectAll')
                                        : t('selectAll')
                                    }
                                </button>
                            </div>
                        )}

                        {/* Content */}
                        <div className={`px-6 py-4 overflow-y-auto ${step === 'selectCards' ? 'max-h-[60vh]' : 'max-h-[350px]'}`}>
                            {step === 'selectDeck' ? (
                                loadingDecks ? (
                                    <div className="flex items-center justify-center py-10">
                                        <Loading />
                                    </div>
                                ) : filteredDecks.length === 0 ? (
                                    <div className="text-center py-10">
                                        <BsFolder2Open className="mx-auto text-gray-300 mb-3" size={48} />
                                        <p className="text-gray-500">
                                            {deckSearchQuery
                                                ? t('noDecksFound')
                                                : t('noOtherDecks')
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredDecks.map(renderDeckItem)}
                                    </div>
                                )
                            ) : (
                                loadingCards ? (
                                    <div className="flex items-center justify-center py-10">
                                        <Loading />
                                    </div>
                                ) : filteredCards.length === 0 ? (
                                    <div className="text-center py-10">
                                        <BsCardText className="mx-auto text-gray-300 mb-3" size={48} />
                                        <p className="text-gray-500">
                                            {cardSearchQuery
                                                ? t('noCardsFound')
                                                : t('noCardsInDeck')
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-1 gap-4">
                                        {filteredCards.map(renderCardItem)}
                                    </div>
                                )
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">
                                    {step === 'selectDeck'
                                        ? t('chooseSourceDeck')
                                        : (selectedCards.length > 0
                                            ? `${selectedCards.length} ${t('cardsSelected')}`
                                            : t('selectCardsToImportHint'))
                                    }
                                </span>
                                <div className="flex gap-3">
                                    <Button variant="secondary" onClick={handleClose}>
                                        {t('cancel')}
                                    </Button>
                                    {step === 'selectCards' && (
                                        <Button
                                            variant="primary"
                                            onClick={handleImport}
                                            disabled={importing || selectedCards.length === 0}
                                        >
                                            {importing ? (
                                                <>
                                                    <Loading />
                                                    {t('importing')}
                                                </>
                                            ) : (
                                                t('import')
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
